import { forwardRef, useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { Box, Flex, Skeleton as ChakraSkeleton } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { Skeleton } from '@material-ui/lab';
import { HistogramChart } from '@mesh.ai/rabbit';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { generateCSVDataApi } from 'api/analytics/goal';
import Carousel from 'components/Carousel';
import CreateGoalPanel from 'components/MeshList/goals/CreateGoalPanel';
import ObjectiveSidePanel from 'components/ObjectiveSidePanel';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOKRInsightsPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRInsightsPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { openCreateOKRPanel } from 'reducers/createObjectivePanelSlice';
import { INSIGHT_VIEWS } from 'reducers/okrsPageSlice';
import { taskReducer } from 'reducers/task';
import { useSelectedView } from 'reducers/viewsSlice';
import { useTeamPageState } from 'screens/Team';
import { okrsInsightsSelector } from 'selectors/okrsInsightsSelector';
import { okrsPageSelector } from 'selectors/okrsPage';
import { baseTheme, themeColors } from 'themes/clients/baseTheme';
import { checkGraphAccessEnabled, checkNewNavEnabled } from 'utils/helpers/featureFlags';
import { checkUserIsGoalAdmin, checkUserIsHRISManager } from 'utils/helpers/rolesAccess';

import { InfoWorkZero, Refresh2 } from 'assets/assetsV1/iconComponents';

import {
  COHORT_TYPES_GOALS,
  GOAL_PROGRESSES,
  GOAL_PROGRESS_VALUE_TO_OBJECT_MAP,
  OKR_TYPES,
  VIEW_DOWNLOAD_INSIGHT_PRIVILEGES,
} from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { isMobileView } from 'constants/helper';
import { localStorageKeys } from 'constants/localStorageKeys';
import {
  sendCreatedKREvent,
  sendCreatedObjectiveEvent,
  sendDownloadCSVEvent,
  sendDownloadCSVGoalsEvent,
} from 'constants/mixpanelHelper';
import { minifyNumber } from 'constants/numberMinifyHelper';

import { AgGridSearch } from 'uiToolkitV1/AgGridWrapperV2';
import { AgGirdDownloadMenu } from 'uiToolkitV1/AgGridWrapperV2/components/DownloadMenu/AgGirdDownloadMenu';
import { AgGridExpandCollapseButton } from 'uiToolkitV1/AgGridWrapperV2/components/ExpandCollapseButton/AgGridExpandCollapseButton';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { Select } from 'uiToolkitV1/Select';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { AgGridAddNewGoalObjectiveSplitButton } from '../AgGridAddNewGoalObjectiveSplitButton';
import ChartContainer from '../AgGridTreeView/components/ChartContainer/ChartContainer';
import { GroupByIndividualGraphs } from '../AgGridTreeView/components/Graphs/GroupByIndividual/GroupByIndividualGraphs';
import { GroupByIndividualMetricsCards } from '../AgGridTreeView/components/MetricsCards/GroupByIndividual/GroupByIndividualMetricsCards';
import { useOKRGroupByViewType } from '../AgGridTreeView/hooks/useOKRGroupByViewType';
import ClickThroughView from './ClickThroughView';
import GroupByIndividualInsightsTable from './RabbitGroupByIndividualInsightsTable';
import GroupByObjectiveInsightsTable from './RabbitGroupByObjectiveInsightsTable';
import useDownloadCSV from './components/useDownloadCSV';
import { convertToCSV, parseCSV } from './helpers';
import {
  downloadReportIndividualInsightsApi,
  downloadReportObjectiveInsightsApi,
  generateReportIndividualInsightsApi,
  generateReportObjectiveInsightsApi,
  getStatusOfIndividualInsightsReportGenerationRequests,
  getStatusOfObjectiveInsightsReportGenerationRequests,
} from './insightsDownloadApis';

const StyledCarousel = styled(Carousel)`
  padding: 8px 0px;
  // margin-bottom: 16px;
`;

const DEFAULT_FILE_NAME = 'InsightsView.csv';

const DEFAULT_GROUP_BY_INDIVIDUAL_FILE_NAME = `IndividualInsights_${Date.now()}.csv`;
const GET_DEFAULT_GROUP_BY_OBJECTIVE_FILE_NAME = (moduleName) => `${moduleName}Insights_${Date.now()}.csv`;

export const GROUP_BY_INDIVIDUAL_KEY_OBJ = {
  uuid: null,
  keyName: 'employeeId',
  displayName: 'Individual',
  enabled: true,
};

export const GROUP_BY_OBJECTIVE_KEY_OBJ = {
  uuid: null,
  keyName: 'objective',
  displayName: 'Objective',
  enabled: true,
};

export const SPLIT_BY_NONE_KEY_OBJ = {
  uuid: null,
  keyName: 'none',
  displayName: 'None',
  enabled: true,
};

export const isDownloadCSVEnabledForCohort = (cohort) =>
  [
    COHORT_TYPES_GOALS.MY_TEAM,
    COHORT_TYPES_GOALS.MY_ORGANIZATION,
    COHORT_TYPES_GOALS.ALL_EMPLOYEE,
    COHORT_TYPES_GOALS.CUSTOM_TYPE,
  ].includes(cohort?.type);

export const GroupByDropdown = ({ selectedOption, handleOptionChange = () => {} }) => {
  const { moduleName } = useOrganisationSettings();

  const updatedInsightViews = INSIGHT_VIEWS;

  updatedInsightViews[1].label = moduleName;

  /* Access Control for insights starts here */
  const { canViewDownloadIndividualInsights, canViewDownloadObjectiveInsights } =
    useOKRInsightsPermissionsBasedOnEmployeeGoalsPrivileges();

  const options = [];

  if (canViewDownloadObjectiveInsights) {
    options.push(INSIGHT_VIEWS[1]);
  }

  if (canViewDownloadIndividualInsights) {
    options.push(INSIGHT_VIEWS[0]);
  }

  /* Access Control for insights ends here */

  return (
    <Flex
      style={{
        width: '7rem',
        zIndex: '5',
      }}
      sx={{
        '.select': {
          width: '100%',
        },
      }}
    >
      <Select
        options={options}
        value={options.find(({ value }) => value === selectedOption)}
        handleChange={(newValue) => handleOptionChange(newValue.value)}
        isSearchable={false}
      />
    </Flex>
  );
};

const PerformanceChart = forwardRef(({ chartData, pluralKeyResultsName, keyResultsName }, ref) => (
  <HistogramChart
    tooltipTitle={`${keyResultsName} Progress Distribution`}
    xAxisLabel="Progress Percentage"
    yAxisLabel={`Count of ${pluralKeyResultsName}`}
    data={chartData}
    integerBins
    integerYAxis
    ref={ref}
  />
));

const OKRInsights = () => {
  const location = useLocation();
  const history = useHistory();
  const { selectedTimelines } = useSelector(okrsPageSelector);
  const employeeGoalsAccess = useSelector((state) => state.employeeGoalsAccessPrivileges) ?? {};
  const [cardKPIData, setCardKPIData] = useState(null);
  const [cardKPIDataGoals, setCardKPIDataGoals] = useState(null);

  const canDownloadIndividualInsights = employeeGoalsAccess.includes(
    VIEW_DOWNLOAD_INSIGHT_PRIVILEGES.DOWNLOAD_INDIVIDUAL_INSIGHTS
  );

  const canDownloadObjectiveInsights = employeeGoalsAccess.includes(
    VIEW_DOWNLOAD_INSIGHT_PRIVILEGES.DOWNLOAD_OBJECTIVE_LEVEL_INSIGHTS
  );

  const selectedCohort = useSelectedView();
  const { viewType, isInsightsClickThroughViewActive } = useSelector(okrsInsightsSelector);

  const dispatch = useDispatch();
  const page = useViewPageName();

  const gridRef = useRef();
  const performanceChartRef = useRef();

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const { team, setTeam, refreshData, setRefreshData, loggedInEmployee, activeFiltersDispatch, newFiltersState } =
    useTeamPageState({ selectedCohort });

  const [isOpenCreateGoalPanel, setIsOpenCreateGoalPanel] = useState(false);
  const [isOpenCreateGoalTreeViewPanel, setIsOpenCreateGoalTreeViewPanel] = useState(false);
  const [isOpenCreateObjectivePanel, setIsOpenCreateObjectivePanel] = useState(false);
  const [timeboundDetailsOfCreatedObjective, setTimeboundDetailsOfCreatedObjective] = useState(null);
  const [isGoalPanelOfCreatedObjective, setIsGoalPanelOfCreatedObjective] = useState(false);
  const [goalsOwnedChartData, setGoalsOwnedChartData] = useState({});
  const [performanceChartData, setPerformanceChartData] = useState({});
  const [tasks, tasksDispatch] = useReducer(taskReducer, []);

  const { setGroupByViewType } = useOKRGroupByViewType();

  const changeCurrentView = (viewType) => {
    setGroupByViewType(viewType);
  };

  const refresh = () => {
    setRefreshData(true);
    setTimeout(() => {
      setRefreshData(false);
    }, 100);
  };

  useEffect(() => {
    refresh();
  }, [selectedTimelines]);

  const handleIndividualDataUpdated = useCallback(() => {
    const totalIndividuals = [];
    gridRef?.current?.api?.forEachNodeAfterFilterAndSort((row) => {
      totalIndividuals.push(row?.data);
    });

    const individualsWithNoCheckIns = totalIndividuals.filter((row) => !row.lastCheckInDate);
    const individualWithZeroKeyResults = totalIndividuals.filter((row) => !row.keyResultCount);
    const sumOfAllProgresses = totalIndividuals.reduce((prev, cur) => (cur?.avgProgress || 0) + prev, 0);
    const avgProgressPercentage = sumOfAllProgresses / (totalIndividuals.length || 1);

    setCardKPIData([
      { title: `Total no. of individuals`, value: totalIndividuals.length },
      {
        title: `Individuals who own no ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`,
        value: individualWithZeroKeyResults.length,
      },
      { title: `Individuals who've never checked-in`, value: individualsWithNoCheckIns.length },
      {
        title: `Average progress percentage`,
        value: `${minifyNumber(Number(avgProgressPercentage || 0))}%`,
      },
    ]);
  }, [keyResultsName]);

  const handleGoalsDataUpdated = useCallback(() => {
    const newRowData = [];
    gridRef?.current?.api?.forEachNodeAfterFilterAndSort((row) => {
      newRowData.push(row?.data);
    });
    const totalKeyResults = newRowData.filter((row) => row.type === OKR_TYPES.KEY_RESULT);
    const keyResultsWithNoCheckIns = totalKeyResults.filter((row) => !row.lastCheckIn);
    const keyResultsAtRisk = totalKeyResults.filter((row) => row.progressLevel === 'AT_RISK');
    const sumOfAllProgresses = totalKeyResults.reduce((prev, cur) => (cur?.avgProgressPercentage || 0) + prev, 0);

    const avgProgressPercentage = sumOfAllProgresses / (totalKeyResults.length || 1);

    setCardKPIDataGoals([
      {
        title: `Total no. of ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`,
        value: totalKeyResults.length,
      },
      {
        title: `No. of ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)} with no check-ins`,
        value: keyResultsWithNoCheckIns.length,
      },
      {
        title: `No. of ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)} at-risk`,
        value: keyResultsAtRisk.length,
      },
      {
        title: `Average progress percentage`,
        value: `${minifyNumber(Number(avgProgressPercentage || 0))}%`,
      },
    ]);
  }, [keyResultsName]);

  const isGoalAdminOrManager = checkUserIsGoalAdmin() || checkUserIsHRISManager();

  const generateCSVData = useCallback(() => {
    sendDownloadCSVGoalsEvent({
      eventProperties: {
        cohortId: selectedCohort?.id,
        cohortName: selectedCohort?.name,
        source: viewType === INSIGHT_VIEWS[0].value ? 'Insight- group by individual' : 'Insight- group by goals',
        groupBy:
          viewType === INSIGHT_VIEWS[0].value
            ? GROUP_BY_INDIVIDUAL_KEY_OBJ.displayName
            : GROUP_BY_OBJECTIVE_KEY_OBJ.displayName,
        splitBy: SPLIT_BY_NONE_KEY_OBJ.displayName,
        type: 'Goal',
        page,
        activeTab: 'Insight',
      },
    });
    return generateCSVDataApi({
      cohort: selectedCohort,
      groupByKeys: viewType === INSIGHT_VIEWS[0].value ? GROUP_BY_INDIVIDUAL_KEY_OBJ : GROUP_BY_OBJECTIVE_KEY_OBJ,
      source: null,
      filter: null,
      timeBounds: selectedTimelines,
    });
  }, [page, selectedCohort, selectedTimelines, viewType]);

  /* Access Control changes start here */

  /* Other revamp helpers start here */

  /* Access Control changes end here */

  /* New access control download apis start here */

  const generateIndividualInsightsCSVDataBE = useCallback(() => {
    sendDownloadCSVEvent({
      eventProperties: {
        source: 'goals',
        page:
          selectedCohort.name === 'My Goals'
            ? `${selectedCohort.name} insights`
            : `Goals ${selectedCohort.name} insights`,
        reportType: 'old report',
      },
    });
    return generateReportIndividualInsightsApi({
      cohort: selectedCohort,
      groupByKeys: GROUP_BY_INDIVIDUAL_KEY_OBJ,
      source: null,
      filter: null,
      timeBounds: selectedTimelines,
    });
  }, [selectedCohort, selectedTimelines]);

  const generateIndividualInsightsCSVData = useCallback(
    ({ exportedRows }) => {
      sendDownloadCSVEvent({
        eventProperties: {
          source: 'goals',
          page:
            selectedCohort?.name === 'My Goals'
              ? `${selectedCohort?.name} insights`
              : `Goals ${selectedCohort?.name} insights`,
          reportType: exportedRows === 'all' ? 'new report detailed' : 'new report filtered',
        },
      });
      gridRef?.current?.api.exportDataAsCsv({
        fileName: DEFAULT_GROUP_BY_INDIVIDUAL_FILE_NAME,
        allColumns: true,
        processCellCallback(params) {
          const { value, node, column } = params ?? {};
          const colData = node?.data;

          if (column?.colId === 'table-last-checkin') {
            return value ? format(new Date(value), 'MMM dd yyyy') : 'Never';
          }
          if (column?.colId === 'table-status') {
            const statusData = colData?.status;

            return Object.keys(statusData)
              .filter((key) => ['ON_TRACK', 'OFF_TRACK', 'CLOSED', 'AT_RISK'].includes(key))
              .map((key) => {
                const label = GOAL_PROGRESSES.find((item) => item.value === key)?.label ?? key;
                return `${label}: ${statusData[key]?.COUNT}`;
              })
              .join(', ');
          }

          return value;
        },

        exportedRows,
      });
    },
    [gridRef, selectedCohort]
  );

  const generateObjectiveInsightsCSVDataBE = useCallback(() => {
    sendDownloadCSVEvent({
      eventProperties: {
        source: 'goals',
        page:
          selectedCohort.name === 'My Goals'
            ? `${selectedCohort.name} insights`
            : `Goals ${selectedCohort.name} insights`,
        reportType: 'old report',
      },
    });
    return generateReportObjectiveInsightsApi({
      cohort: selectedCohort,
      groupByKeys: GROUP_BY_OBJECTIVE_KEY_OBJ,
      source: null,
      filter: null,
      timeBounds: selectedTimelines,
    });
  }, [selectedCohort, selectedTimelines]);

  const generateObjectiveInsightsCSVData = useCallback(
    ({ exportedRows }) => {
      sendDownloadCSVEvent({
        eventProperties: {
          source: 'goals',
          page:
            selectedCohort.name === 'My Goals'
              ? `${selectedCohort.name} insights`
              : `Goals ${selectedCohort.name} insights`,
          reportType: exportedRows === 'all' ? 'new report detailed' : 'new report filtered',
        },
      });
      const data = gridRef?.current?.api.getDataAsCsv({
        fileName: GET_DEFAULT_GROUP_BY_OBJECTIVE_FILE_NAME(moduleName),
        allColumns: true,
        processCellCallback(params) {
          const { value, node, column } = params ?? {};
          const colData = node?.data;

          if (column?.colId === 'ag-Grid-AutoColumn') {
            return value?.title;
          }

          if (column?.colId === 'table-status') {
            const progressLabel =
              GOAL_PROGRESS_VALUE_TO_OBJECT_MAP[colData?.progressLevel]?.label ?? colData?.progressLevel;

            return `${progressLabel}`;
          }
          if (column?.colId === 'employee') {
            return value?.title;
          }

          if (column?.colId === 'table-type') {
            if (value === OKR_TYPES.OBJECTIVE) {
              return modifyString(objectivesName, StringModifier.TITLE_CASE);
            }
            return modifyString(keyResultsName, StringModifier.TITLE_CASE);
          }

          if (['table-owner', 'table-collaborators'].includes(column?.colId)) {
            const { toIds } = node.data;

            if (column?.colId === 'table-owner') {
              return toIds
                ?.map(({ email, ownerType }) => {
                  if (ownerType === 'OWNER') return email;
                  return null;
                })
                ?.filter((item) => item)
                .join(', ');
            }
            if (column?.colId === 'table-collaborators') {
              return toIds
                ?.map(({ email, ownerType }) => {
                  if (ownerType === 'COLLABORATOR') return email;
                  return null;
                })
                ?.filter((item) => item)
                .join(', ');
            }
          }
          if (
            ['table-start-date', 'table-due-date', 'table-last-checkin', 'table-last-checkin-children'].includes(
              column?.colId
            )
          ) {
            if (column?.colId === 'table-start-date') {
              const startDate = colData?.startDate;
              return startDate ? format(new Date(startDate), 'MMM dd yyyy') : 'Never';
            }
            if (column?.colId === 'table-due-date') {
              const dueDate = colData?.dueDate;
              return dueDate ? format(new Date(dueDate), 'MMM dd yyyy') : 'Never';
            }
            if (column?.colId === 'table-last-checkin') {
              const lastCheckin = colData?.lastCheckin;
              return lastCheckin ? format(new Date(lastCheckin), 'MMM dd yyyy') : 'Never';
            }
            if (column?.colId === 'table-last-checkin-children') {
              const lastCheckin = colData?.latestChildCheckInTS;
              return lastCheckin ? format(new Date(lastCheckin), 'MMM dd yyyy') : 'Never';
            }
          }
          if (column?.colId === 'table-progress') {
            // get only the number from the string
            return value?.match(/\d+/g)?.[0];
          }
          return value;
        },
        exportedRows,
      });

      const parsedCsv = parseCSV(data);

      const updatedCsv = parsedCsv.map((row, index) => {
        // deleting keys Goal Title, Progress %age from row object

        delete row['"Goal Title"'];
        delete row['"Progress %age"\r'];

        return row;
      });

      const csvData = convertToCSV(updatedCsv);
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = GET_DEFAULT_GROUP_BY_OBJECTIVE_FILE_NAME(moduleName);
      a.click();
    },
    [moduleName, selectedCohort?.name]
  );

  /* New access control download apis ends here */

  const { generateFile: generateIndividualInsightsFile } = useDownloadCSV({
    downloadFileHandler: generateIndividualInsightsCSVDataBE,
    localStorageKey: localStorageKeys.individualInsightsCSVDownload,
    fileName: DEFAULT_GROUP_BY_INDIVIDUAL_FILE_NAME,
    getStatusOfRequestIds: getStatusOfIndividualInsightsReportGenerationRequests,
    downloadCSVApi: downloadReportIndividualInsightsApi,
  });

  const { generateFile: generateGoalsInsightsFile } = useDownloadCSV({
    downloadFileHandler: generateObjectiveInsightsCSVDataBE,
    localStorageKey: localStorageKeys.objectiveInsightsCSVDownload,
    fileName: GET_DEFAULT_GROUP_BY_OBJECTIVE_FILE_NAME(modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)),
    getStatusOfRequestIds: getStatusOfObjectiveInsightsReportGenerationRequests,
    downloadCSVApi: downloadReportObjectiveInsightsApi,
  });

  /* Other revamp helpers start here */
  const RefreshIconComponent = () => <Refresh2 boxSize={8} />;
  /* Other revamp helpers end here */

  if (isInsightsClickThroughViewActive) return <ClickThroughView />;

  return (
    <Box
      position="relative"
      height="auto"
      className="teamPageContainer"
      mt={!checkNewNavEnabled() ? 0 : 1}
      p={!checkNewNavEnabled() ? 4 : 0}
    >
      {/* {selectedTimelines?.length > 0 ? (
        <GoalCharts selectedTimeBoundPeriod={selectedTimelines?.[0] ?? null} />
      ) : null} */}

      {selectedTimelines?.length > 0 ? (
        <Box
          borderRadius="8px"
          p="0px"
          // backgroundColor={themeColors.neutral.EBEFF3}
        >
          {!isMobileView() &&
            checkGraphAccessEnabled() &&
            (viewType === INSIGHT_VIEWS[0].value ? (
              <GroupByIndividualGraphs
                goalsOwnedChartData={goalsOwnedChartData}
                gridRef={gridRef}
                keyResultsName={keyResultsName}
              />
            ) : (
              <ChartContainer
                chartData={performanceChartData}
                chartRef={performanceChartRef}
                chartTitle={`${modifyString(keyResultsName, StringModifier.TITLE_CASE)} Progress Distribution`}
              >
                <PerformanceChart
                  chartData={
                    performanceChartData?.rowData
                      ?.filter((data) => data.type === OKR_TYPES.KEY_RESULT)
                      ?.map((data) => data?.avgProgressPercentage || 0) || []
                  }
                  pluralKeyResultsName={modifyString(keyResultsName, StringModifier.PLURAL_TITLE_CASE)}
                  keyResultsName={modifyString(keyResultsName, StringModifier.TITLE_CASE)}
                />
              </ChartContainer>
            ))}
          {!isMobileView() && checkGraphAccessEnabled() && viewType === INSIGHT_VIEWS[0].value && cardKPIData && (
            <GroupByIndividualMetricsCards cardKPIData={cardKPIData} isLoading={goalsOwnedChartData?.isLoading} />
          )}
          {!isMobileView() && checkGraphAccessEnabled() && viewType === INSIGHT_VIEWS[1].value && cardKPIDataGoals && (
            <>
              {performanceChartData?.isLoading && (
                <Box display="flex">
                  <ChakraSkeleton
                    height="100px"
                    width="100%"
                    borderRadius="8px"
                    key={index}
                    startColor="surface.F5F5F5"
                    endColor="surface.g3"
                  />
                </Box>
              )}
              {!performanceChartData?.isLoading && (
                <MetricCards height="100%" columns="repeat(4, 1fr)" cardTitle="Metrics" cards={cardKPIDataGoals} />
              )}

              {/* {cardKPIDataGoals.map((data, index) =>
                 performanceChartData?.isLoading ? (
                 
                 ) : (
                   <CardKPI count={data.value} title={data.title} unstyledDimension />
                 )
               )}
               <StyledCarousel slidesToShow={4} slidesToScroll={1} slideMargin="10px" stretchChildren></StyledCarousel> */}
            </>
          )}
          <PaperWithShadow p="12px" width="100%" borderRadius="8px" mt={1}>
            <Flex direction="column">
              <Flex alignItems="center" justifyContent="space-between">
                <Box mr="auto" w="360px">
                  <AgGridSearch
                    key={selectedCohort?.id}
                    gridRef={gridRef}
                    inputGroupProps={{ minW: '380px', maxW: '380px' }}
                    placeholder={
                      viewType === INSIGHT_VIEWS[0].value
                        ? 'Search across individuals, check-in count, progress etc.'
                        : 'Search across title, check-in count, progress etc.'
                    }
                  />
                </Box>
                <Flex gap={3}>
                  <AgGridAddNewGoalObjectiveSplitButton
                    onAddKRClick={() => {
                      dispatch(
                        openCreateOKRPanel({
                          selectedObjective: null,
                          selectedTimeBoundPeriod: selectedTimelines?.[0],
                          okrPanelType: OKR_TYPES.KEY_RESULT,
                          onKeyResultCreation(okrDetails) {
                            sendCreatedKREvent({
                              eventProperties: {
                                source: 'Add New Button',
                                page,
                                goalId: okrDetails?.uuid,
                              },
                            });
                            refresh();
                          },
                          onObjectiveCreation(okrDetails) {
                            sendCreatedObjectiveEvent({
                              eventProperties: {
                                source: 'Add New Button',
                                page,
                                objectiveId: okrDetails?.uuid,
                                description: okrDetails?.description,
                                level: okrDetails?.objectiveLevel,
                                type: okrDetails?.type,
                                timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
                              },
                            });
                            refresh();
                          },
                          history,
                          location,
                        })
                      );
                    }}
                    onAddObjectiveClick={() => {
                      dispatch(
                        openCreateOKRPanel({
                          selectedObjective: null,
                          selectedTimeBoundPeriod: selectedTimelines?.[0],
                          okrPanelType: OKR_TYPES.OBJECTIVE,
                          onKeyResultCreation(okrDetails) {
                            sendCreatedKREvent({
                              eventProperties: {
                                source: 'Add New Button',
                                page,
                                goalId: okrDetails?.uuid,
                              },
                            });
                            refresh();
                          },
                          onObjectiveCreation(okrDetails) {
                            sendCreatedObjectiveEvent({
                              eventProperties: {
                                source: 'Add New Button',
                                page,
                                objectiveId: okrDetails?.uuid,
                                description: okrDetails?.description,
                                level: okrDetails?.objectiveLevel,
                                type: okrDetails?.type,
                                timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
                              },
                            });
                            refresh();
                          },
                          history,
                          location,
                        })
                      );
                    }}
                  />
                  {viewType === INSIGHT_VIEWS[1].value ? (
                    <AgGridExpandCollapseButton defaultExpandAll gridRef={gridRef} onExpandCollapse={() => {}} />
                  ) : null}

                  {/* COMMENTING THIS TEMPORARILY */}
                  {/* <Box mx={1}>
              <Button
                StartIcon={RefreshIconComponent}
                onClick={() => {
                  queryClient.invalidateQueries([
                    REACT_QUERY_KEYS.GoalsInsightsGroupByIndividualData
                  ]);
                  queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
                }}
                variant="tertiary"
              />
            </Box> */}

                  <>
                    {viewType === INSIGHT_VIEWS[0].value && canDownloadIndividualInsights ? (
                      <AgGirdDownloadMenu
                        options={[
                          {
                            title: 'Current View Download (new version)',
                            description: `Download selected metrics for filtered list of ${modifyString(
                              moduleName,
                              StringModifier.PLURAL_SENTENCE_CASE
                            )} along with basic ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} details`,
                            onClick: () =>
                              generateIndividualInsightsCSVData({
                                exportedRows: 'filteredAndSorted',
                              }),
                            isDisabled: false,
                          },
                          {
                            title: 'Detailed Download (new version)',
                            description: `Download all the metrics for all the ${modifyString(
                              moduleName,
                              StringModifier.PLURAL_SENTENCE_CASE
                            )} along with basic ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} details`,
                            onClick: () =>
                              generateIndividualInsightsCSVData({
                                exportedRows: 'all',
                              }),
                            isDisabled: false,
                          },
                          {
                            title: `Detailed Download (Old Version)`,
                            description: 'Download all the individual with basic details',
                            onClick: generateIndividualInsightsFile,
                          },
                        ]}
                      />
                    ) : null}
                    {viewType === INSIGHT_VIEWS[1].value && canDownloadObjectiveInsights ? (
                      <AgGirdDownloadMenu
                        options={[
                          {
                            title: 'Current View Download (new version)',
                            description: `Download all the ${modifyString(
                              moduleName,
                              StringModifier.PLURAL_SENTENCE_CASE
                            )} with details and basic metrics`,
                            onClick: () =>
                              generateObjectiveInsightsCSVData({
                                exportedRows: 'filteredAndSorted',
                              }),
                            // variant: 'ag-grid'
                          },
                          {
                            title: 'Detailed Download (new version)',
                            description: `Download all the metrics for all the ${modifyString(
                              moduleName,
                              StringModifier.PLURAL_SENTENCE_CASE
                            )} along with basic ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} details`,
                            onClick: () =>
                              generateObjectiveInsightsCSVData({
                                exportedRows: 'all',
                              }),
                            // variant: 'ag-grid'
                          },
                          {
                            title: `Detailed Download (old version)`,
                            description: `Download selected metrics for filtered list of ${modifyString(
                              moduleName,
                              StringModifier.PLURAL_SENTENCE_CASE
                            )} along with basic ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} details`,
                            // variant: 'api',
                            // fileName: DEFAULT_GROUP_BY_INDIVIDUAL_FILE_NAME,
                            onClick: generateGoalsInsightsFile,
                          },
                        ]}
                      />
                    ) : null}
                  </>
                </Flex>
              </Flex>
              <Flex alignItems="center" height="64px" padding="4px">
                <Flex padding="8px 12px 8px 12px">
                  <TextBodyMedium color={themeColors.text.g3} bold mr={4}>
                    Group by :
                  </TextBodyMedium>
                  <GroupByDropdown selectedOption={viewType} handleOptionChange={changeCurrentView} />
                </Flex>
              </Flex>
            </Flex>
            {viewType === INSIGHT_VIEWS[0].value ? (
              <GroupByIndividualInsightsTable
                selectedCohort={selectedCohort}
                gridRef={gridRef}
                selectedTimeBoundPeriods={selectedTimelines}
                selectedGroupByKey={GROUP_BY_INDIVIDUAL_KEY_OBJ}
                selectedSplitByKey={SPLIT_BY_NONE_KEY_OBJ}
                setChartData={setGoalsOwnedChartData}
                onModalUpdated={handleIndividualDataUpdated}
              />
            ) : (
              <GroupByObjectiveInsightsTable
                gridRef={gridRef}
                selectedTimelines={selectedTimelines}
                selectedCohort={selectedCohort}
                groupBy={GROUP_BY_OBJECTIVE_KEY_OBJ}
                selectedSplitByKey={SPLIT_BY_NONE_KEY_OBJ}
                setChartData={setPerformanceChartData}
                onModalUpdated={handleGoalsDataUpdated}
              />
            )}
          </PaperWithShadow>

          <Flex
            alignItems="center"
            mt="16px"
            borderRadius="8px"
            padding="8px"
            backgroundColor="#F5F7F9"
            boxShadow={baseTheme.shadows.paper}
          >
            <InfoWorkZero boxSize="24px" />
            <TextBodyMedium>
              You will see metrics only for the {modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)} you've
              access to, hence for same individual or {modifyString(objectivesName, StringModifier.SENTENCE_CASE)},
              admin could see a different metrics due to difference in access level
            </TextBodyMedium>
          </Flex>
        </Box>
      ) : (
        <Skeleton height="30vh" />
      )}

      <CreateGoalPanel
        open={isOpenCreateGoalPanel}
        onCancel={() => {
          setIsOpenCreateGoalPanel(false);
        }}
        goalListDispatch={tasksDispatch}
        employee={loggedInEmployee}
        subObjective={isGoalPanelOfCreatedObjective ? { ...timeboundDetailsOfCreatedObjective } : null}
        source="teampage"
        eventSource="Team Page"
        selectedTimeBoundPeriod={selectedTimelines?.[0] ?? null}
        afterGoalCreation={refresh}
      />

      <ObjectiveSidePanel
        open={isOpenCreateObjectivePanel}
        onCancel={() => {
          setTimeboundDetailsOfCreatedObjective(null);
          setIsGoalPanelOfCreatedObjective(false);
          setIsOpenCreateObjectivePanel(false);
        }}
        selectedTimeBoundPeriod={selectedTimelines?.[0] ?? null}
        isAlignmentAtParentLevel
        setIsOpenCreateGoalPanel={(value) => {
          setIsOpenCreateGoalPanel(value);
          setIsGoalPanelOfCreatedObjective(value);
        }}
        setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
        source="Team Page"
      />
    </Box>
  );
};

export default OKRInsights;
